import { z } from 'zod'
import { Role } from '~/utils/shared/roles'

/**
 * Tipo de disparador baseado em requisição HTTP, incluindo o método e a rota.
 */
export type HttpTrigger = {
  trigger: 'http'
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  route: `/${string}`
  server?: 'serverless' | 'main' // default is serverless
  manualResponse?: boolean
}

/**
 * Tipo de disparador baseado em WebSocket, incluindo a rota de conexão.
 */
export type WebsocketTrigger = {
  trigger: 'websocket'
  route: string
}

/**
 * Tipo de disparador acionado por alterações em algum recurso (ou sistema) específico.
 */
export type ChangeTrigger = {
  trigger: 'change'
}

/**
 * Tipo de disparador agendado (cron), definido por meio de uma expressão cron.
 */
export type CronTrigger = {
  trigger: 'cron'
  expression: string
}

/**
 * União de todos os tipos possíveis de disparadores.
 */
export type Trigger = HttpTrigger | WebsocketTrigger | ChangeTrigger | CronTrigger

/**
 * Definição de cópia de arquivos/diretórios.
 * Ex.: copiar arquivos de uma pasta "from" para outra "to".
 */
export type Copy = { from: string; to: string }

/**
 * Controles de escala do runtime serverless.
 */
export type RuntimeScale = {
  /**
   * Quantidade mínima de instâncias sempre prontas.
   * O padrão é 0 para manter scale-to-zero.
   */
  minimumInstances?: number

  /**
   * Quantidade máxima de instâncias que a ação pode abrir em burst.
   */
  maximumInstances?: number

  /**
   * Quantidade máxima de requisições simultâneas por instância.
   * O padrão conservador é 1, principalmente para runtimes com CPU fracionada.
   */
  maximumConcurrentRequests?: number
}

/**
 * BaseTrigger pode ser tanto um objeto com uma string genérica
 * de trigger quanto um trigger específico já definido acima.
 */
export type BaseTrigger =
  | {
      trigger: string
    }
  | Trigger

/**
 * Interface base para definição de uma ação.
 * Contém informações genéricas como tipo, chave (key), descrição,
 * configurações de runtime, disparadores e acesso.
 */
export interface BaseActionDefinition {
  type: string
  key: string
  description: string
  runtime: {
    platform: string
    memory: number
    long?: {
      machineType?: string
      storage?: number
      fileShareServers?: {
        serverName: string
      }[]
      attachedDisks?: string[]
    }
    extraLibraries?: string[]
    copy?: Copy[]
    timeout?: number
    minimumInstances?: RuntimeScale['minimumInstances']
    maximumInstances?: RuntimeScale['maximumInstances']
    maximumConcurrentRequests?: RuntimeScale['maximumConcurrentRequests']
  }
  triggers: BaseTrigger[]
  access?: string
}

/**
 * Interface para ações que têm 'type' = 'action'.
 * Inclui disparadores mais específicos, restrições de acesso e
 * detalhes adicionais de runtime.
 */
export interface ActionDefinition<
  PlatformRuntime extends 'python312' | 'nodejs24' = 'python312' | 'nodejs24',
> extends BaseActionDefinition {
  type: 'action'
  key: string
  description: string
  triggers: Trigger[]
  runtime: {
    extraLibraries?: string[]
    copy?: Copy[]
    timeout?: number
    minimumInstances?: RuntimeScale['minimumInstances']
    maximumInstances?: RuntimeScale['maximumInstances']
    maximumConcurrentRequests?: RuntimeScale['maximumConcurrentRequests']
    platform: PlatformRuntime
    memory: number
    long?: {
      machineType?: string
      storage?: number
      fileShareServers?: {
        serverName: string
      }[]
      attachedDisks?: string[]
      customDockerfile?: boolean
    }
  }
  access?: 'public' | 'private' | 'internal' | 'dataSource'
  restrictedToRoles?: Role[]
}

/**
 * Configurações de cache, com possíveis configurações
 * para cache frontal (front) e traseiro (back).
 */
export interface Cache {
  front?: {
    /**
     * Tempo, em milissegundos, após o qual uma atualização é necessária.
     * Antes desse tempo, os dados são resgatados do cache front.
     */
    expirationMsForUpdate?: number

    /**
     * Em caso de falha ao resgatar dados, define por quanto tempo os dados
     * ainda poderão ser usados ao invés de retornar erro, em milissegundos.
     */
    expirationMsForFallback?: number

    /**
     * Intervalo, em milissegundos, para atualização automática dos dados
     * (por exemplo, para telas que ficam muito tempo paradas).
     */
    autoUpdateMs?: number
  }
  back?: {
    /**
     * Tempo, em milissegundos, após o qual uma atualização é necessária.
     * Antes desse tempo, os dados são resgatados do cache traseiro.
     */
    expirationMsForUpdate?: number

    /**
     * Em caso de falha ao resgatar dados, define por quanto tempo os dados
     * ainda poderão ser usados ao invés de retornar erro, em milissegundos.
     */
    expirationMsForFallback?: number
  }
}

/**
 * Interface estendida de BaseActionDefinition para suportar schemas Zod
 * de entrada (input) e saída (output), além de permitir configuração de cache.
 *
 * @template InputSchema Tipo de esquema Zod para o input
 * @template OutputSchema Tipo de esquema Zod para o output
 */
export interface BaseActionDefinitionEnhanced<
  InputSchema extends z.ZodType<any, any>,
  OutputSchema extends z.ZodType<any, any>,
> extends BaseActionDefinition {
  /**
   * Esquema Zod que valida o formato de entrada (input) desta ação.
   */
  input: InputSchema

  /**
   * Esquema Zod que valida o formato de saída (output) desta ação.
   */
  output: OutputSchema

  /**
   * (Opcional) Função para gerar configurações de cache,
   * com base no input validado pelo Zod.
   */
  cache?: (input: z.infer<InputSchema>) => Cache
}

/**
 * Interface estendida de ActionDefinition para suportar schemas Zod
 * de entrada (input) e saída (output), além de permitir configuração de cache.
 *
 * @template InputSchema Tipo de esquema Zod para o input
 * @template OutputSchema Tipo de esquema Zod para o output
 */
export interface ActionDefinitionEnhanced<
  InputSchema extends z.ZodType<any, any>,
  OutputSchema extends z.ZodType<any, any>,
> extends ActionDefinition {
  /**
   * Esquema Zod que valida o formato de entrada (input) desta ação.
   */
  input: InputSchema

  /**
   * Esquema Zod que valida o formato de saída (output) desta ação.
   */
  output: OutputSchema

  /**
   * (Opcional) Função para gerar configurações de cache,
   * com base no input validado pelo Zod.
   */
  cache?: (input: z.infer<InputSchema>) => Cache
}

/**
 * Função de utilidade para criar uma definição de ação
 * (com schemas Zod para entrada e saída) e retornar
 * o próprio objeto de definição inalterado.
 *
 * @param actionDefinition Objeto que segue a interface BaseActionDefinitionEnhanced
 * @returns O mesmo objeto, preservando o tipo (para inferência)
 */
export default function defineAction<
  InputSchema extends z.ZodType<any, any>,
  OutputSchema extends z.ZodType<any, any>,
>(
  actionDefinition: BaseActionDefinitionEnhanced<InputSchema, OutputSchema>,
): BaseActionDefinitionEnhanced<InputSchema, OutputSchema> {
  return actionDefinition
}
